/**
 * /app/$tenantSlug/posh/training/$moduleId/quiz — quiz runner for a
 * training module.
 *
 * Gated: the quiz opens only once every scenario is done (or the module
 * is already completed) — the same rule as the module card and the
 * server (409 scenarios_incomplete). Otherwise "Finish the scenarios
 * first" with a link back to the player.
 */

import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { localize } from "@/lib/api";
import { useTrainingModules } from "@/hooks/use-posh";
import { PageHeader } from "@/components/shared/page-header";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { QuizLockedNotice, QuizRunner } from "@/components/posh/quiz-runner";
import { TrainingLanguageSwitch } from "@/components/posh/training-language-switch";
import { TrainingErrorAlert } from "@/components/posh/training-error-alert";
import { quizUnlocked } from "@/components/posh/training-player-logic";
import { poshText } from "@/components/posh/posh-ui-text";
import { usePoshLang } from "@/lib/posh-lang";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/posh/training/$moduleId/quiz",
)({
  component: QuizRoute,
});

function QuizSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-7 w-1/2" />
      <Skeleton className="h-48 w-full rounded-xl" />
    </div>
  );
}

function QuizBody({ moduleId }: { moduleId: number }) {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const modulesQ = useTrainingModules();
  const { lang } = usePoshLang();

  if (modulesQ.isLoading) return <QuizSkeleton />;

  if (modulesQ.isError) {
    return (
      <TrainingErrorAlert
        title={poshText("loadModuleError", lang)}
        error={modulesQ.error}
        lang={lang}
        onRetry={() => void modulesQ.refetch()}
      />
    );
  }

  const entry = modulesQ.data?.modules.find((m) => m.module.id === moduleId);

  if (!entry) {
    return (
      <Alert lang={lang}>
        <AlertTitle>{poshText("moduleNotFound", lang)}</AlertTitle>
        <AlertDescription className="space-y-3">
          <p>{poshText("moduleNotFoundBody", lang)}</p>
          <Button asChild variant="outline" size="sm" className="min-h-11 sm:min-h-9">
            <Link to="/app/$tenantSlug/posh/training" params={{ tenantSlug }}>
              {poshText("backToTraining", lang)}
            </Link>
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const { module, progress, scenario_total } = entry;
  const unlocked = quizUnlocked(
    progress?.status,
    progress?.scenarios_completed?.length ?? 0,
    scenario_total ?? 0,
  );

  return (
    <div lang={lang}>
      <div className="mb-4">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="-ml-2 min-h-11 gap-1.5 text-muted-foreground sm:min-h-9"
        >
          <Link
            to="/app/$tenantSlug/posh/training/$moduleId"
            params={{ tenantSlug, moduleId: String(module.id) }}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            {localize(module.title, lang) || module.code}
          </Link>
        </Button>
      </div>
      <PageHeader
        title={poshText("quizTitle", lang)}
        description={poshText("quizDescription", lang, { p: module.passing_score })}
        actions={<TrainingLanguageSwitch />}
      />
      {unlocked ? (
        <QuizRunner module={module} />
      ) : modulesQ.isFetching ? (
        // A just-finished module may still be refetching — don't flash
        // the lock at someone who has done every scenario.
        <QuizSkeleton />
      ) : (
        <QuizLockedNotice moduleId={module.id} lang={lang} />
      )}
    </div>
  );
}

function QuizRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { moduleId } = Route.useParams();
  const { lang } = usePoshLang();
  const id = Number(moduleId);

  return (
    <PoshFeatureGate>
      {Number.isFinite(id) && id > 0 ? (
        <QuizBody moduleId={id} />
      ) : (
        <Alert variant="destructive" lang={lang}>
          <AlertTitle>{poshText("invalidModule", lang)}</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>{poshText("invalidModuleBody", lang)}</p>
            <Button asChild variant="outline" size="sm" className="min-h-11 sm:min-h-9">
              <Link to="/app/$tenantSlug/posh/training" params={{ tenantSlug }}>
                {poshText("backToTraining", lang)}
              </Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}
    </PoshFeatureGate>
  );
}
